import { ODataCollectionResponseV4 } from "@odata2ts/odata-core";
import { ODataQueryBuilderV4 } from "@odata2ts/odata-query-builder";
import { RequireExactlyOne } from "type-fest";
import {
  isApiFetchErrorResponse,
  isApiMutationErrorResponse,
  ResponseError,
} from "./errors";
import { getEnvSettings } from "./utils";

export type ODataServices =
  | "AppU4DM"
  | "Document"
  | "Equipment"
  | "Task"
  | "MaterialTraceability";

const odataService = (service: ODataServices) => {
  const { baseUrl, sitHost } = getEnvSettings();
  return `${baseUrl}/sit-svc/${sitHost}/Application/${service}/odata/`;
};

type Method = "GET" | "POST";

type ODataFetchParams<TParam> = RequireExactlyOne<
  {
    path: string;
    builder: ODataQueryBuilderV4<any>;
    token: string;
    service?: ODataServices;
    method?: Method;
    body?: TParam;
  },
  "path" | "builder"
>;

type ODataFetchResponse<T, TMethod> = TMethod extends "POST"
  ? T
  : ODataCollectionResponseV4<T>;

export async function odataFetch<T, TParam = object>(
  params: ODataFetchParams<TParam> & { method: "POST" }
): Promise<T>;
export async function odataFetch<T, TParam = object>(
  params: ODataFetchParams<TParam> & { method?: "GET" }
): Promise<ODataCollectionResponseV4<T> | undefined>;

// Implementation
export async function odataFetch<T, TParam = object>({
  path,
  builder,
  token,
  service = "AppU4DM",
  method = "GET",
  body,
}: ODataFetchParams<TParam> & { method?: "GET" | "POST" }): Promise<
  ODataFetchResponse<T, typeof method>
> {
  const fullPath = path ?? builder.build();
  const res = await fetch(odataService(service) + fullPath, {
    method,
    body: JSON.stringify(body),
    headers: {
      Authorization: "Bearer " + token,
      Accept: "application/json",
      ...(body ? { "content-type": "application/json;charset=UTF-8" } : {}),
    },
  });

  if (!res.ok) throw await createErrorResponse(res);

  return res.json() as ODataFetchResponse<T, typeof method>;
}

export async function odataFetchBlob({
  service = "AppU4DM",
  path,
  token,
}: Pick<ODataFetchParams<any>, "service" | "path" | "token">) {
  const res = await fetch(odataService(service) + path, {
    headers: {
      Authorization: "Bearer " + token,
      Accept: "application/json",
    },
  });

  if (!res.ok) throw await createErrorResponse(res);

  return res.blob();
}

async function createErrorResponse(response: Response) {
  try {
    const error = await response.json();

    if (isApiFetchErrorResponse(error)) {
      return new ResponseError(
        error.error.message,
        response.status,
        error.error.code
      );
    }

    if (isApiMutationErrorResponse(error)) {
      return new ResponseError(
        error.Error.ErrorMessage,
        response.status,
        error.Error.ErrorCode
      );
    }

    return new ResponseError(response.statusText, response.status);
  } catch {
    return new ResponseError(response.statusText, response.status);
  }
}
